import clsx from 'clsx'
import dayjs from 'dayjs'
import { useId, type ReactNode } from 'react'
import type { Control } from 'react-hook-form'

import Checkbox from '@components/atoms/form/Checkbox'
import { Switch } from '@components/atoms/Switch'
import AsyncSelect from '@components/molecules/AsyncSelect'
import Icon from '@components/utils/Icon'
import { CallbackNote } from '@frontend/contexts/users_management/components/CallbackNote'
import { FLEXIBLE_WINDOW_MINUTES, relativeDay, timeRange, whenRange } from '@utils/callbackTime'
import type { SelectOption } from '@utils/types'

/**
 * The schedule-callback fields, shared by the Calls preview (in a dialog) and
 * the calling session preview (embedded in the log-call form). Sample-data
 * only: nothing here persists.
 */

export type CallbackDraft = {
  date: string
  time: string
  // OK to call a bit before or after the time
  flexible: boolean
  // Keep them out of other calling sessions and call lists until this is done
  assigneeId: string
  notes: string
}

export type CallbackAssignee = { id: string; name: string }
// Who an admin can assign to: other admins, or the group's leaders.
export type AssignablePerson = CallbackAssignee & { kind: 'admin' | 'leader' }

// Value of the "Assigned to" option that leaves it for any group leader.
export const ANYONE = 'anyone'

const pad = (n: number) => String(n).padStart(2, '0')
export const toDateInput = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
export const toTimeInput = (d: Date) => `${pad(d.getHours())}:${pad(d.getMinutes())}`
export const callbackDueAt = (draft: CallbackDraft) => new Date(`${draft.date}T${draft.time}`)

// Evenings are when supporters with day jobs pick up, so default to 6pm:
// tonight if it's still ahead, otherwise tomorrow.
export function newCallbackDraft(now: Date, assigneeId: string, notes = ''): CallbackDraft {
  const day = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  if (now.getHours() >= 17) day.setDate(day.getDate() + 1)
  return { date: toDateInput(day), time: '18:00', flexible: false, assigneeId, notes }
}

export const draftWhen = (draft: CallbackDraft, now: Date) =>
  whenRange(callbackDueAt(draft).toISOString(), now, draft.flexible)

export type CallbackOwner = 'me' | 'anyone' | 'other'
export const callbackOwner = (draft: CallbackDraft, meId: string): CallbackOwner =>
  draft.assigneeId === meId ? 'me' : draft.assigneeId === ANYONE ? 'anyone' : 'other'

export const ownersCallback = (owner: CallbackOwner, ownerName: string) =>
  owner === 'me' ? 'Your callback' : owner === 'other' ? `${ownerName}'s callback` : 'The callback'

export const assignedLabel = (owner: CallbackOwner, ownerName: string) =>
  owner === 'me' ? 'assigned to you' : owner === 'other' ? `assigned to ${ownerName}` : 'any leader'

export function existingCallbackNotice(
  firstName: string,
  draft: CallbackDraft,
  ownerName: string,
  meId: string,
  now: Date
) {
  const owner = callbackOwner(draft, meId)
  const by = owner === 'anyone' ? 'open to any leader' : assignedLabel(owner, ownerName)
  return `${firstName} already has a callback for ${draftWhen(draft, now)}, ${by}. Saving moves it; there is only ever one.`
}

const capitalise = (text: string) => text.charAt(0).toUpperCase() + text.slice(1)

export function CallbackCard({
  callback,
  ownerName,
  meId,
  now,
  children,
}: {
  callback: CallbackDraft
  ownerName: string
  meId: string
  now: Date
  children?: ReactNode
}) {
  return (
    <div className="tw:max-w-xl tw:rounded-lg tw:border tw:border-primary-200 tw:bg-primary-50/40 tw:px-4 tw:py-3 tw:text-sm">
      <div className="tw:flex tw:flex-wrap tw:items-baseline tw:justify-between tw:gap-x-3 tw:gap-y-0.5">
        <p className="tw:flex tw:items-center tw:gap-1.5 tw:font-semibold tw:text-gray-900">
          <Icon type="calendar-check-01" className="tw:h-4 tw:w-4 tw:shrink-0 tw:text-primary-600" aria-hidden />
          Callback {draftWhen(callback, now)}
        </p>
        <p className="tw:text-gray-600">{capitalise(assignedLabel(callbackOwner(callback, meId), ownerName))}</p>
      </div>
      {callback.notes && <p className="tw:mt-2 tw:text-gray-700">{callback.notes}</p>}
      {children && <div className="tw:mt-3 tw:border-t tw:border-primary-200 tw:pt-3">{children}</div>}
    </div>
  )
}

export function SkipInSessionSwitch({
  firstName,
  checked,
  onCheckedChange,
}: {
  firstName: string
  checked: boolean
  onCheckedChange: (checked: boolean) => void
}) {
  const id = useId()
  return (
    <div className="tw:flex tw:items-start tw:gap-3">
      <Switch id={id} aria-describedby={`${id}-hint`} checked={checked} onCheckedChange={onCheckedChange} />
      <div>
        <label htmlFor={id} className="tw:m-0 tw:block tw:cursor-pointer tw:text-sm tw:font-medium tw:text-gray-900">
          Skip in this session
        </label>
        <p id={`${id}-hint`} className="tw:m-0 tw:text-xs tw:text-gray-true-500">
          {checked
            ? `${firstName} won't be called in this session until the callback is done.`
            : `${firstName} is back in this session's queue and can be called now.`}
        </p>
      </div>
    </div>
  )
}

function flexibleHint(draft: CallbackDraft) {
  const start = callbackDueAt(draft)
  if (Number.isNaN(start.getTime())) return 'Shows as a one-hour window starting at this time.'
  return `Shows as a one-hour window starting at this time (${timeRange(start.toISOString(), true)}). Call any time in that hour.`
}

export function callbackDraftError(draft: CallbackDraft, now: Date) {
  if (!draft.date || !draft.time || Number.isNaN(callbackDueAt(draft).getTime())) return 'Pick a date and a time.'
  if (callbackDueAt(draft) <= now) return 'That time has already passed. Pick a later one.'
  return null
}

export const inputClass =
  'tw:w-full tw:rounded-lg tw:border tw:border-gray-300 tw:bg-white tw:px-3 tw:py-2 tw:text-sm tw:text-gray-900 tw:focus:border-primary-500 tw:focus:outline-hidden tw:focus:ring-4 tw:focus:ring-primary-100'
export const labelClass = 'tw:text-sm tw:font-medium tw:text-gray-700'

const CheckField = ({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  description: string
}) => (
  <label className="tw:flex tw:cursor-pointer tw:items-start tw:gap-3">
    <input
      type="checkbox"
      className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:accent-primary-600"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
    />
    <span className="tw:flex tw:flex-col">
      <span className={labelClass}>{label}</span>
      <span className="tw:text-sm tw:text-gray-500">{description}</span>
    </span>
  </label>
)

/**
 * Who the callback is assigned to.
 * - admin: pick any admin or group leader, or leave it for the whole group.
 * - leader: no picking other leaders, only "me" or "the group" (e.g. when they
 *   can't make the time), as a pair of radio options.
 * Leave it out and there's no field: it's assigned to whoever schedules it.
 */
export type AssignConfig =
  | { mode: 'admin'; people: AssignablePerson[]; meId: string; groupName: string }
  | { mode: 'leader'; meId: string; meName: string; groupName: string }

// A searchable select styled like the rest of the app's async selects. The
// options are local here; in the product they'd come from a query.
export function LocalAsyncSelect({
  name,
  label,
  options,
  value,
  onChange,
  placeholder,
  isClearable = false,
}: {
  name: string
  label: string
  options: SelectOption[]
  value: SelectOption | null
  onChange: (option: SelectOption | null) => void
  placeholder?: string
  isClearable?: boolean
}) {
  const loadOptions = async (input: string) =>
    options.filter((o) => o.label.toLowerCase().includes(input.trim().toLowerCase()))

  return (
    <div className="tw:flex tw:flex-col">
      <AsyncSelect
        name={name}
        displayName={label}
        loadOptions={loadOptions}
        debounceSearch={false}
        defaultOptions={options}
        value={value ?? undefined}
        onChange={(option) => onChange((option as SelectOption | null) ?? null)}
        isCreatable={false}
        isMulti={false}
        isClearable={isClearable}
        placeholder={placeholder}
        noOptionsMessage="No matches"
        // Portaled so the menu isn't clipped by a scrolling dialog.
        menuPortalTarget={typeof document === 'undefined' ? null : document.body}
        menuPosition="fixed"
      />
    </div>
  )
}

function AssignField({
  assign,
  draft,
  onChange,
}: {
  assign: AssignConfig
  draft: CallbackDraft
  onChange: (draft: CallbackDraft) => void
}) {
  if (assign.mode === 'admin') {
    const options: SelectOption[] = [
      { value: ANYONE, label: `Any leader · ${assign.groupName}` },
      ...assign.people.map((p) => ({
        value: p.id,
        label: `${p.name}${p.id === assign.meId ? ' (me)' : ''} · ${p.kind === 'admin' ? 'Admin' : 'Group leader'}`,
      })),
    ]
    return (
      <LocalAsyncSelect
        name="callbackAssignee"
        label="Assigned to"
        options={options}
        value={options.find((o) => o.value === draft.assigneeId) ?? null}
        onChange={(option) => option && onChange({ ...draft, assigneeId: option.value })}
        placeholder="Search admins and leaders"
      />
    )
  }

  const toGroup = draft.assigneeId === ANYONE
  const setToGroup = (group: boolean) => onChange({ ...draft, assigneeId: group ? ANYONE : assign.meId })

  const choices = [
    { group: false, label: `Me (${assign.meName})`, hint: 'It goes on your callbacks. Only you will call.' },
    {
      group: true,
      label: 'Any leader',
      hint: `For when you can't make that time. Every ${assign.groupName} leader will see it.`,
    },
  ]
  return (
    <fieldset className="tw:flex tw:flex-col tw:gap-2">
      <legend className={clsx(labelClass, 'tw:mb-2')}>Who should call back?</legend>
      <div className="tw:grid tw:gap-2 tw:sm:grid-cols-2">
        {choices.map((c) => (
          <label
            key={String(c.group)}
            className={clsx(
              'tw:flex tw:cursor-pointer tw:items-start tw:gap-3 tw:rounded-lg tw:border tw:bg-white tw:p-3',
              toGroup === c.group ? 'tw:border-primary-500 tw:ring-2 tw:ring-primary-100' : 'tw:border-gray-300'
            )}
          >
            <input
              type="radio"
              name="callbackAssignTo"
              className="tw:mt-0.5 tw:h-4 tw:w-4 tw:shrink-0 tw:accent-primary-600"
              checked={toGroup === c.group}
              onChange={() => setToGroup(c.group)}
            />
            <span className="tw:flex tw:flex-col">
              <span className={labelClass}>{c.label}</span>
              <span className="tw:text-sm tw:text-gray-500">{c.hint}</span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  )
}

export function ScheduleCallbackFields({
  draft,
  onChange,
  now,
  assign,
}: {
  draft: CallbackDraft
  onChange: (draft: CallbackDraft) => void
  now: Date
  assign?: AssignConfig
}) {
  const error = callbackDraftError(draft, now)

  return (
    <div className="tw:flex tw:flex-col tw:gap-4">
      {assign && <AssignField assign={assign} draft={draft} onChange={onChange} />}

      <div className="tw:flex tw:flex-col tw:gap-2">
        <div className="tw:grid tw:grid-cols-[minmax(0,1fr)_10rem] tw:gap-3">
          <label className="tw:flex tw:flex-col tw:gap-1">
            <span className={labelClass}>Callback date</span>
            <input
              type="date"
              required
              aria-invalid={!!error}
              className={inputClass}
              min={toDateInput(now)}
              value={draft.date}
              onChange={(e) => onChange({ ...draft, date: e.target.value })}
            />
          </label>
          <label className="tw:flex tw:flex-col tw:gap-1">
            <span className={labelClass}>Callback time</span>
            <input
              type="time"
              required
              step={900}
              aria-invalid={!!error}
              className={inputClass}
              value={draft.time}
              onChange={(e) => onChange({ ...draft, time: e.target.value })}
            />
          </label>
        </div>
        {error && (
          <p role="alert" className="tw:flex tw:items-center tw:gap-1.5 tw:text-sm tw:text-error-700">
            <Icon type="alert-circle" className="tw:h-4 tw:w-4 tw:shrink-0" />
            {error}
          </p>
        )}
      </div>

      <CheckField
        checked={draft.flexible}
        onChange={(flexible) => onChange({ ...draft, flexible })}
        label="Call is flexible"
        description={flexibleHint(draft)}
      />

      <label className="tw:flex tw:flex-col tw:gap-1">
        <span className={labelClass}>Callback note</span>
        <textarea
          className={inputClass}
          rows={3}
          value={draft.notes}
          onChange={(e) => onChange({ ...draft, notes: e.target.value })}
        />
      </label>
    </div>
  )
}

export function CompleteCallbackField({
  control,
  callback,
  assigneeName,
  now,
}: {
  control: Control<any>
  callback: CallbackDraft
  assigneeName: string
  now: Date
}) {
  const due = dayjs(callbackDueAt(callback))
  const end = due.add(callback.flexible ? FLEXIBLE_WINDOW_MINUTES : 0, 'minute')
  const overdue = end.isBefore(now)
  const tone = due.isSame(now, 'day') ? 'today' : due.isBefore(now, 'day') ? 'past' : 'future'
  const relative = relativeDay(due.toISOString(), now)
  const anyone = callback.assigneeId === ANYONE

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3 tw:rounded-lg tw:border tw:border-primary-200 tw:bg-primary-50/40 tw:p-4">
      <div className="tw:flex tw:flex-wrap tw:items-center">
        <Checkbox name="completeCallback" control={control}>
          Mark callback complete
        </Checkbox>
      </div>
      <div className="tw:overflow-hidden tw:rounded-md tw:border tw:border-gray-200 tw:bg-white tw:text-sm">
        <div
          className={clsx(
            'tw:border-b tw:border-gray-200 tw:bg-gray-50 tw:px-4 tw:py-2 tw:font-semibold',
            tone === 'today' && 'tw:text-primary-700',
            tone === 'past' && 'tw:text-error-700',
            tone === 'future' && 'tw:text-gray-700'
          )}
        >
          {relative && `${relative} · `}
          {due.format('dddd D MMMM')}
        </div>
        <div className="tw:flex tw:items-center tw:gap-4 tw:px-4 tw:py-3">
          <span className="tw:flex tw:shrink-0 tw:items-center tw:gap-3">
            <span
              aria-hidden
              className={clsx(
                'tw:h-2.5 tw:w-2.5 tw:rounded-full',
                overdue ? 'tw:bg-error-500' : tone === 'today' ? 'tw:bg-primary-500' : 'tw:bg-gray-300'
              )}
            />
            <span
              className={clsx(
                'tw:whitespace-nowrap tw:font-semibold tw:tabular-nums',
                overdue ? 'tw:text-error-600' : 'tw:text-gray-900'
              )}
            >
              {timeRange(due.toISOString(), callback.flexible)}
              {overdue && <span className="tw:sr-only"> (overdue)</span>}
            </span>
          </span>
          <div className="tw:min-w-0 tw:flex-1">{callback.notes && <CallbackNote note={callback.notes} />}</div>
          <span
            className={clsx(
              'tw:inline-flex tw:max-w-[40%] tw:shrink-0 tw:items-center tw:gap-1 tw:rounded-full tw:px-2 tw:py-0.5 tw:text-xs tw:font-medium',
              anyone ? 'tw:bg-gray-100 tw:text-gray-700' : 'tw:bg-primary-50 tw:text-primary-700'
            )}
          >
            <Icon type={anyone ? 'users-01' : 'user-01'} className="tw:h-3.5 tw:w-3.5 tw:shrink-0" aria-hidden />
            <span className="tw:truncate">{assigneeName}</span>
          </span>
        </div>
      </div>
    </div>
  )
}
